/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ServiceExclusivity } from '@acp/shared';
import { ServiceExclusivityOption, ServiceOption } from './types';
import { defaultServiceExclusivityOptions, defaultServiceOptions } from './constants';

function getAppliedServiceLabel({
    serviceExclusivity,
    serviceOptions = defaultServiceOptions,
    services,
}: {
    serviceOptions: ServiceOption[];
    services: string[];
    serviceExclusivity: ServiceExclusivity;
}) {
    const appliedServices = services.map(
        (service) => serviceOptions.find((option) => option.value === service)?.label
    );

    const serviceLabels = [...appliedServices];

    let serviceLabel = '';

    if (serviceExclusivity === ServiceExclusivity.Including) {
        if (serviceLabels.length === 1) {
            serviceLabel = `with at least "${serviceLabels[0]}" services`;
        }

        if (serviceLabels.length === 2) {
            serviceLabel = `with at least "${serviceLabels.join(' and ')}" services`;
        }

        if (serviceLabels.length === 3) {
            serviceLabel = `with at least "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels[serviceLabels.length - 1]
            }" services`;
        }

        if (serviceLabels.length > 3) {
            serviceLabel = `with at least "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels.length - 2
            } more" services`;
        }
    }

    if (serviceExclusivity === ServiceExclusivity.Only) {
        if (serviceLabels.length === 1) {
            serviceLabel = `with only "${serviceLabels[0]}" services`;
        }

        if (serviceLabels.length === 2) {
            serviceLabel = `with only "${serviceLabels.join(' and ')}" services`;
        }

        if (serviceLabels.length === 3) {
            serviceLabel = `with only "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels[serviceLabels.length - 1]
            }" services`;
        }

        if (serviceLabels.length > 3) {
            serviceLabel = `with only "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels.length - 2
            } more" services`;
        }
    }

    if (serviceExclusivity === ServiceExclusivity.Excluding) {
        if (serviceLabels.length === 1) {
            serviceLabel = `without "${serviceLabels[0]}" services`;
        }

        if (serviceLabels.length === 2) {
            serviceLabel = `without "${serviceLabels.join(' and ')}" services`;
        }

        if (serviceLabels.length === 3) {
            serviceLabel = `without "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels[serviceLabels.length - 1]
            }" services`;
        }

        if (serviceLabels.length > 3) {
            serviceLabel = `without "${serviceLabels.slice(0, 2).join(', ')}, and ${
                serviceLabels.length - 2
            } more" services`;
        }
    }

    return serviceLabel;
}

function useAppliedServiceLabel({
    serviceExclusivity,
    serviceOptions = defaultServiceOptions,
    services,
}: {
    serviceOptions?: ServiceOption[];
    services: string[];
    serviceExclusivity: ServiceExclusivity;
}) {
    return useMemo(
        () => getAppliedServiceLabel({
            serviceExclusivity,
            serviceOptions,
            services,
        }),
        [serviceExclusivity, serviceOptions, services]
    );
}

function useServiceFilter(serviceOptions: ServiceOption[] = defaultServiceOptions, {
    onChange = () => {},
}: {
    onChange?: () => void;
} = {}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const isValidService = (service: string) => serviceOptions.some(
        // get options
        (option) => option.value === service
    );

    const setService = (newService: string, isChecked: boolean) => {
        if (!isValidService(newService)) return;

        setSearchParams(
            (params) => {
                const services = params.getAll('service');

                if (isChecked && services.includes(newService)) return params;
                if (!isChecked && !services.includes(newService)) return params;

                if (isChecked) {
                    params.append('service', newService);
                } else {
                    params.delete('service', newService);
                }

                return params;
            },
            {
                replace: true,
            }
        );

        onChange();
    };

    const services = useMemo(
        () => searchParams.getAll('service').filter(isValidService),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchParams]
    );

    return {
        setService,
        services,
    };
}

function useServiceExclusivityFilter(
    serviceExclusivityOptions: ServiceExclusivityOption[] = defaultServiceExclusivityOptions,
    {
        onChange = () => {},
    }: {
        onChange?: () => void;
    } = {}
) {
    const [searchParams, setSearchParams] = useSearchParams();

    const isValidServiceExclusivity = (
        //
        exclusivity: ServiceExclusivity
    ) => serviceExclusivityOptions.some((option) => option.value === exclusivity);

    const setServiceExclusivity = (exclusivity: ServiceExclusivity) => {
        if (!isValidServiceExclusivity(exclusivity)) return;

        setSearchParams(
            (params) => {
                if (exclusivity === null) {
                    params.delete('service-exclusivity');
                    return params;
                }

                params.set('service-exclusivity', exclusivity);

                return params;
            },
            {
                replace: true,
            }
        );

        onChange();
    };

    const serviceExclusivity = useMemo(() => {
        const param = searchParams.get('service-exclusivity');

        if (param === null) {
            // This is the default value we use when the param is not set
            return ServiceExclusivity.Including;
        }

        return param;
    }, [searchParams]) as ServiceExclusivity;

    return {
        serviceExclusivity,
        setServiceExclusivity,
    };
}

export { useServiceFilter, useAppliedServiceLabel, useServiceExclusivityFilter };
